import { cn } from "@lib/cn"
import { useTranslation } from "react-i18next"

import type { Material, MaterialStatus } from "@/types"

// PRD-29 — TYPE_ICONS moved to the shared @lib/materials so the
// students feature can reuse it without a cross-feature import.
// Re-exported here so existing `from "./statusVisuals"` imports in the
// materials feature keep working.
export { DEFAULT_TYPE_ICON, TYPE_ICONS } from "@lib/materials"

export type EffectiveStatus = MaterialStatus | "borrador_failed"

/**
 * Material.status is what the backend stores; the UI distinguishes the
 * "all-students-failed" terminal case from a quiet draft by checking
 * the per-student aggregate counts. PRD-11 replaces the old questions-
 * based heuristic with assignments_failed > 0 && assignments_ready === 0.
 *
 * `revision` is covered too (PRD-59 Etapa A): the backend flips a material
 * to "pending approval" when its run ends, including a run where every
 * student failed. Left as `revision`, that row read "Pendiente de aprobar"
 * and offered "Aprobar y enviar" with nothing to send. The name stays
 * `borrador_failed` because every consumer — lanes, card, row, home — already
 * renders it as "Falló" with the recovery affordances, which is what a
 * dead `revision` needs as well.
 */
export function effectiveStatus(
  material: Pick<Material, "status" | "assignments_failed" | "assignments_ready">,
): EffectiveStatus {
  if (
    (material.status === "borrador" || material.status === "revision") &&
    material.assignments_failed > 0 &&
    material.assignments_ready === 0
  ) {
    return "borrador_failed"
  }
  return material.status
}

interface StatusStyle {
  badgeClass: string
  dotClass: string
}

const STATUS_STYLES: Record<EffectiveStatus, StatusStyle> = {
  borrador: {
    badgeClass: "bg-inset text-ink-muted",
    dotClass: "bg-border-strong",
  },
  borrador_failed: {
    badgeClass: "bg-danger-soft text-danger-ink",
    dotClass: "bg-danger",
  },
  // Historical only since PRD-42 (scheduled *generation* is retired).
  // Demoted to a neutral tone so the one status a professor can still
  // act on — `por_enviar` — owns the indigo "scheduled" accent.
  programado: {
    badgeClass: "bg-inset text-ink-muted",
    dotClass: "bg-border-strong",
  },
  // PRD-42 §FR-3.2 — approved, waiting for its send day.
  por_enviar: {
    badgeClass: "bg-accent-soft text-accent-ink",
    dotClass: "bg-accent",
  },
  procesando: {
    // Indigo is the one "in flight" hue product-wide (PRD-58 phase D);
    // the pulse, not a second colour, is what separates it from por_enviar.
    badgeClass: "bg-accent-soft text-accent-ink",
    dotClass: "bg-accent animate-pulse",
  },
  revision: {
    badgeClass: "bg-attention-soft text-attention-ink",
    dotClass: "bg-attention",
  },
  enviado: {
    badgeClass: "bg-success-soft text-success-ink",
    dotClass: "bg-success",
  },
  cancelado: {
    badgeClass: "bg-inset text-ink-subtle",
    dotClass: "bg-border-strong",
  },
}

export function statusStyle(status: EffectiveStatus): StatusStyle {
  return STATUS_STYLES[status]
}

// ──────────────────────────────────────────────────────────
// Grading badge (PRD-44 follow-up)
// ──────────────────────────────────────────────────────────
//
// A sent material's status badge stays a flat "Enviado" for its entire
// life, whether nobody has submitted yet or every submission is
// waiting on the professor's review — the only signal today is a small
// strip (`GradingCompletionStrip`) that has to be read carefully, and
// the material detail page has no equivalent at all. This second badge
// answers "does THIS sent material still need me?" from the same
// fields the card already fetches (`grading_completion`,
// `pending_review_count`) — no extra request, no new backend field.

export type GradingBadgeKind = "pending_review" | "all_graded" | "partial" | "not_submitted"

export interface GradingBadgeInfo {
  kind: GradingBadgeKind
  /** The number to interpolate: pending_review's count, or `graded` for
   *  `partial`/`all_graded`, or `total` for `not_submitted`. */
  count: number
  total: number
}

/**
 * Only meaningful for a sent, gradeable material with at least one
 * student on the roster — mirrors the gate `GradingCompletionStrip`
 * already applies (`MaterialCard.tsx`), so the two never disagree on
 * when to render.
 */
export function gradingBadge(
  material: Pick<
    Material,
    "status" | "creates_grade_record" | "grading_completion" | "pending_review_count"
  >,
): GradingBadgeInfo | null {
  if (material.status !== "enviado" || !material.creates_grade_record) return null
  const { graded, total } = material.grading_completion
  if (total === 0) return null
  if (material.pending_review_count > 0) {
    return { kind: "pending_review", count: material.pending_review_count, total }
  }
  if (graded === total) return { kind: "all_graded", count: graded, total }
  if (graded > 0) return { kind: "partial", count: graded, total }
  return { kind: "not_submitted", count: total, total }
}

const GRADING_BADGE_STYLE: Record<GradingBadgeKind, string> = {
  pending_review: "bg-attention-soft text-attention-ink",
  all_graded: "bg-success-soft text-success-ink",
  partial: "bg-accent-soft text-accent-ink",
  not_submitted: "bg-inset text-ink-subtle",
}

const GRADING_BADGE_DOT: Record<GradingBadgeKind, string> = {
  pending_review: "bg-attention",
  all_graded: "bg-success",
  partial: "bg-accent",
  not_submitted: "bg-border-strong",
}

interface GradingStatusBadgeProps {
  material: Pick<
    Material,
    "status" | "creates_grade_record" | "grading_completion" | "pending_review_count"
  >
  className?: string
}

/**
 * Renders nothing when `gradingBadge()` returns null — every call site
 * can render it unconditionally next to the status pill. Reuses the
 * exact i18n keys the card's strip and the review queue already show
 * (`grading:actions.needs_review.badge`, `materials:card.grading.*`,
 * `grading:queue.summary.not_submitted`) instead of new copy.
 */
export function GradingStatusBadge({ material, className }: GradingStatusBadgeProps) {
  const { t } = useTranslation(["materials", "grading"])
  const info = gradingBadge(material)
  if (!info) return null

  const label =
    info.kind === "pending_review"
      ? t("grading:actions.needs_review.badge", { count: info.count })
      : info.kind === "all_graded"
        ? t("materials:card.grading.all_done")
        : info.kind === "partial"
          ? `${info.count}/${info.total} ${t("materials:card.grading.label")}`
          : t("grading:queue.summary.not_submitted")

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        GRADING_BADGE_STYLE[info.kind],
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", GRADING_BADGE_DOT[info.kind])} aria-hidden />
      {label}
    </span>
  )
}
